<template>
  <div class="min-h-screen">
    <Transition>
      <LoadingView :is-loading="isLoading" />
    </Transition>

    <RouterView v-if="!isLoading" />
  </div>
  <footer class="container mx-auto mb-16 text-center text-gray-700">
    <p class="m-0 p-0">&copy; DEMV</p>
  </footer>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useGlobalStore } from '@/features/Global/store';
import LoadingView from '@/views/Loading/index.vue';
import { useBrokerStore } from '@/features/Broker/store';
import { useCurrentStep } from '@/utils/useCurrentRoute/useCurrentStep';
import { ClientResource, ROLE } from '@/features/Clients';
import { useEventBus } from '@/utils/useEventBus/useEventBus';
import { getApiHttpClientInstance } from '@/services/api/useApi';
import { useContextParameters } from '@/router/utils/useContextParameters';

const showInitialLoadingState = ref(true);

useRouter().afterEach(() => {
  nextTick(() => {
    showInitialLoadingState.value = false;
  });
});

const globalStore = useGlobalStore();
const brokerStore = useBrokerStore();
const route = useRoute();

const isLoading = computed(() => {
  if (route.name === 'login') {
    return true;
  }

  if (route.name === 'Error') {
    return false;
  }

  if (showInitialLoadingState.value) {
    return true;
  }

  if (!globalStore.sparte) {
    return false;
  }

  if (globalStore.isWebreport) {
    return false;
  }

  if (globalStore.isVersicherungsangabenContext) {
    return false;
  }

  const broker = brokerStore.broker.data;

  return broker?.id === undefined;
});

const currentStep = useCurrentStep();

watch(
  () => route.name,
  (r) => {
    currentStep.updateCurrentStep(r);
  },
  {
    immediate: true,
  },
);

const router = useRouter();
const resetBedarfAndClient = () => {
  brokerStore.resetBedarfInformation();

  const queryParams = { ...route.query };
  delete queryParams.bedarfId;
  delete queryParams.clientId;
  router.replace({ query: queryParams });

  getApiHttpClientInstance().setBedarfId(undefined);
  useContextParameters().queryParamMap.delete('clientId');
};

const checkIfClientMatchesBedarfClient = (client: ClientResource) => {
  if (brokerStore.bedarfInformation.clientId !== undefined) {
    if (client.id !== Number(brokerStore.bedarfInformation.clientId)) {
      return true;
    }
  }

  return false;
};

const eventBus = useEventBus();

eventBus.on('clientManuallySelected', ({ client, role }) => {
  if (
    role === ROLE.VERSICHERUNGSNEHMER &&
    !checkIfClientMatchesBedarfClient(client)
  ) {
    resetBedarfAndClient();
  }
});

eventBus.on('clientDeSelected', (role) => {
  if (role === ROLE.VERSICHERUNGSNEHMER) {
    resetBedarfAndClient();
  }
});
</script>

<style scoped>
.v-enter-active,
.v-leave-active {
  transition: opacity 0.2s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
